import styled, { keyframes } from 'styled-components';
import { SystemNotifyPosition } from 'context/SystemNotifyContext';
import { color } from 'styles/pallete';

const toastInRight = keyframes`
	0% {
		opacity: 0;
		transform: translate3d(2.4rem, -0.8rem, 0) scale(0.98);
	}

	100% {
		opacity: 1;
		transform: translate3d(0, 0, 0) scale(1);
	}
`;

const toastOutRight = keyframes`
	0% {
		opacity: 1;
		transform: translate3d(0, 0, 0) scale(1);
	}

	100% {
		opacity: 0;
		transform: translate3d(2.4rem, -0.8rem, 0) scale(0.98);
	}
`;

const toastInLeft = keyframes`
	0% {
		opacity: 0;
		transform: translate3d(-2.4rem, -0.8rem, 0) scale(0.98);
	}

	100% {
		opacity: 1;
		transform: translate3d(0, 0, 0) scale(1);
	}
`;

const toastOutLeft = keyframes`
	0% {
		opacity: 1;
		transform: translate3d(0, 0, 0) scale(1);
	}

	100% {
		opacity: 0;
		transform: translate3d(-2.4rem, -0.8rem, 0) scale(0.98);
	}
`;

const toastInCenter = keyframes`
	0% {
		opacity: 0;
		transform: translate3d(0, -1.6rem, 0) scale(0.98);
	}

	100% {
		opacity: 1;
		transform: translate3d(0, 0, 0) scale(1);
	}
`;

const toastOutCenter = keyframes`
	0% {
		opacity: 1;
		transform: translate3d(0, 0, 0) scale(1);
	}

	100% {
		opacity: 0;
		transform: translate3d(0, -1.6rem, 0) scale(0.98);
	}
`;

const progressAnimation = keyframes`
	0% {
		transform: scaleX(1);
	}

	100% {
		transform: scaleX(0);
	}
`;

function getToastAnimation(position: SystemNotifyPosition, isOpen: boolean) {
	if (position === 'left') {
		return isOpen ? toastInLeft : toastOutLeft;
	}

	if (position === 'right') {
		return isOpen ? toastInRight : toastOutRight;
	}

	return isOpen ? toastInCenter : toastOutCenter;
}

export const container = styled.div<{ $position: SystemNotifyPosition }>`
	position: fixed;
	top: 2rem;
	left: ${(props) => (props.$position === 'left' ? '2rem' : 'auto')};
	right: ${(props) => (props.$position === 'right' ? '2rem' : 'auto')};
	${(props) =>
		props.$position === 'center' &&
		`
			left: 50%;
			transform: translateX(-50%);
		`}
	z-index: 10000;
	width: min(42rem, calc(100vw - 4rem));
	pointer-events: none;

	@media (max-width: 538px) {
		top: 1rem;
		right: 1rem;
		left: 1rem;
		transform: none;
		width: auto;
	}
`;

export const toastList = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

export const toast = styled.div<{
	$isOpen: boolean;
	$position: SystemNotifyPosition;
	$typeNotify: string;
}>`
	position: relative;
	width: 100%;
	min-height: 5.8rem;
	display: grid;
	grid-template-columns: auto minmax(0, 1fr) auto;
	align-items: center;
	gap: 1rem;
	overflow: hidden;
	padding: 1.2rem 1.4rem 1.4rem;
	border-radius: 5px;
	background-color: ${color.interface.white};
	border-left: 5px solid ${(props) => props.$typeNotify};
	box-shadow:
		0 1.2rem 3rem rgba(0, 0, 0, 0.16),
		0 0.2rem 0.8rem rgba(0, 0, 0, 0.08);
	animation: ${(props) => getToastAnimation(props.$position, props.$isOpen)}
		0.35s cubic-bezier(0.21, 1.02, 0.73, 1) forwards;
	pointer-events: auto;

	@media (max-width: 538px) {
		min-height: 5.4rem;
		padding: 1rem 1.2rem 1.2rem;
	}
`;

export const icon = styled.div<{ $typeNotify: string }>`
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 2.2rem;
	line-height: 1;
	color: ${(props) => props.$typeNotify};

	svg {
		display: block;
	}
`;

export const message = styled.p`
	min-width: 0;
	color: ${color.interface.darkgray3};
	font-size: 1.4rem;
	line-height: 1.35;
	font-family: nunito-bold;
	word-break: break-word;
`;

export const closeButton = styled.button`
	all: unset;
	width: 2.8rem;
	height: 2.8rem;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 5px;
	color: ${color.interface.lightGray};
	font-size: 1.8rem;
	cursor: pointer;
	transition:
		background-color 0.2s ease,
		color 0.2s ease,
		transform 0.2s ease;

	:hover {
		background-color: ${color.interface.lightGray4};
		color: ${color.interface.darkgray3};
		transform: scale(1.04);
	}
`;

export const progress = styled.div<{
	$isOpen: boolean;
	$typeNotify: string;
	$duration: number;
}>`
	position: absolute;
	right: 0;
	bottom: 0;
	left: 0;
	height: 0.35rem;
	background-color: ${(props) => props.$typeNotify};
	transform-origin: left center;
	animation: ${(props) => (props.$isOpen ? progressAnimation : 'none')} 3s
		linear forwards;
	animation-duration: ${(props) => props.$duration}ms;
`;
